/**
 * InspectionSeriesListing — modern Inspection Series listing using FilterBar + Quimby Table
 * (Table owns the Toolbar, selection state, and ColumnVisibilityControl).
 *
 * Replaces the legacy jQuery Kendo Grid implementation in index.jsx.
 *
 * Preserved behaviors:
 *   - All current column cells: id link, building/account link-cells with self-link guard,
 *     referenceNumber, contractNumber, frequencies, expirationDate, systemAsset,
 *     inspectionsCount, status (HTML label), autoExtend.
 *   - FilterBar with all Series filters (building, account, territory, expirationDate,
 *     status, autoExtend, expiredSeries) plus Inspections-parity Search (IP-15819).
 *     Filters always visible per prop flags. Search is non-persistent.
 *   - Toolbar: Toolbar.Selection (count + Select all N entirely) + Bulk Extend
 *     (disabled with no selection); ColumnVisibilityControl pinned at the far right.
 *     Selection and toolbar are now owned by Table (plan/table-owned-toolbar-selection).
 *   - Header checkbox selects the current page; Select all in the bar is
 *     select-all-entirely. Selection clears on filter/sort/search/page changes via resetKey.
 *   - Bulk Extend: populates existing `#inspection_series_ids` hidden field from the Rails
 *     ERB modal with page-selected IDs, or the full matching set when select-all-entirely
 *     is active (re-fetch at pageSize = totalCount). Then shows the Bootstrap modal.
 *     1–10 year validation and form submission behavior preserved without duplicate handlers.
 *   - View, Cancel, Extend row actions using Rails remote/modal flows (data-remote="true").
 *   - Localized loading, empty, and retryable error states.
 *   - Sticky selector column (Table's selectable prop), sticky id (primary), sticky actions (action).
 *   - Page sizes [25, 50, 100, 250].
 *   - Legacy localStorage preference migration via useSeriesPreferences.
 *   - Column visibility with immediate checkbox updates and Reset.
 *
 * IP-15819 supersedes the original "no search / page-only only" Series contract
 * for flag-ON chrome. Search and select-all-entirely now match Inspections.
 *
 * Scope contexts:
 *   - standalone: buildingId=null, accountId=null
 *   - building:   buildingId is set
 *   - account:    accountId is set
 *
 * Hook adoption (task-40):
 *   - A1  useListingScopeFromProps
 *   - A2  useListingPreferenceBootstrap
 *   - A3  usePersistedListingPreferences
 *   - A4  createListingLookupLoaders — skipped; paged-cursor loader type incompatible with factory
 *   - A5  useListingFilterBarControllers
 *   - A6  useDebouncedListingSearch
 *   - A7  useListingTableControllers
 *   - A8  superseded (deprecated)
 *   - A9  superseded — selection now lives in Table via useTableSelection (plan TOS task-04)
 *   - A10 useListingColumnVisibilityState
 *   - A11 useListingQueryViewModel (with getTotalCount for Series shape)
 *   - A12–A15 skipped — no equivalent inline edit / mass-action / context bridge in Series
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, TableSize } from '@ui/molecules/Table/Table';
import { Button } from '@ui/atoms/Button';
import { showSuccessToast } from '@ui/molecules/Toast';
import { FilterBar } from '@ui/organisms/FilterBar';
import { WorkspaceHeader } from '@ui/molecules/Header/WorkspaceHeader/WorkspaceHeader';
import I18n, { translate } from '@utils/i18n';
import { loadSelectedFromOptions } from '@helpers/lookupByIds';
import { KendoIntlProvider } from '@contexts/kendo-intl-context';
import { useListingQueryState, useListingScopeFromProps, useListingPreferenceBootstrap, usePersistedListingPreferences, useListingFilterBarControllers, useListingTableControllers, useDebouncedListingSearch, useListingColumnVisibilityState, useListingQueryViewModel, usePublishListContextBridge } from '@hooks/listings';
import { INITIAL_SERIES_FILTERS, SERIES_PAGE_SIZES } from './types';
import EditEndDateModal from '../EditEndDateModal';
import { useSeriesPreferences } from './hooks/useSeriesPreferences';
import { useSeriesFilterConfig } from './hooks/useSeriesFilterConfig';
import { useSeriesQuery } from './hooks/useSeriesQuery';
import { columnPolicyForScope, SERIES_NON_SORTABLE_KEYS } from './adapters/seriesColumnPolicy';
import { createArrayLoader } from './adapters/createArrayLoader';
import { createBuildingsLoader, createAccountsLoader, createBuildingsByIdsLoader, createAccountsByIdsLoader } from './services/InspectionSeriesLookupService';
import { fetchInspectionSeries } from './services/InspectionSeriesService';
import { buildVisibleSeriesColumns, buildSeriesColumnVisibilityItems } from './components/SeriesColumns';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const NS = 'inspection_series.datatable';
const DEFAULT_SORT = { field: 'id', direction: 'desc' };
const BULK_EXTEND_YEARS_MIN = 1;
const BULK_EXTEND_YEARS_MAX = 10;
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function InspectionSeriesListing({ buildingId = null, accountId = null, pageSize: pageSizeProp = 25, territoriesForSelect = [], showHeader = false, pageTitle = '', headerSubtitle = '', newSeriesUrl = null, canCreateSeries = false, newSeriesLabel = '', newSeriesForbiddenMessage = '', hasShowBuildingFilter = true, hasShowAccountFilter = true, hasShowExpirationDateFilter = true, hasShowStatusFilter = true, hasShowAutoExtendFilter = true, hasShowExpiredSeriesFilter = true, hasDisableAllFilters = false, hasDisableAllBulkActions = false, minEndDate = '', maxEndDate = '' }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    // Row whose expiration date is being edited. Held here rather than in the row so the modal
    // survives the grid re-rendering underneath it.
    const [editingSeries, setEditingSeries] = useState(null);
    // --- A1: Scope ---
    const scope = useListingScopeFromProps({ buildingId, accountId });
    // --- Preferences (page-specific; wraps useListingPreferences with Series cache key) ---
    const { preferences, savePreferences, clearPreferences } = useSeriesPreferences({ scope });
    // --- Column policy (page-specific) ---
    const policy = useMemo(() => columnPolicyForScope(scope), [scope]);
    // --- A2: Bootstrap initial state values from preferences ---
    const { initialVisibleColumnKeys, initialPageSize, initialSort, initialFilters } = useListingPreferenceBootstrap({
        preferences,
        defaultActiveFilterKeys: [],
        columnPolicy: policy,
        defaultFilters: INITIAL_SERIES_FILTERS,
        defaultSort: DEFAULT_SORT,
        defaultPageSize: pageSizeProp,
        includeSearch: true
    });
    const { state, setFilters, setSearch, setSort, setPageNumber, setPageSize } = useListingQueryState({
        scope,
        page: { number: 1, size: initialPageSize },
        sort: initialSort,
        filters: initialFilters,
        search: ''
    });
    // --- Loaders (A4 skipped: paged-cursor loader type incompatible with factory's LoadOptionsFn) ---
    const loadBuildings = useMemo(() => createBuildingsLoader(), []);
    const loadAccounts = useMemo(() => createAccountsLoader(), []);
    const loadTerritories = useMemo(() => createArrayLoader(territoriesForSelect.map((t) => ({ value: t.value, label: t.label }))), [territoriesForSelect]);
    const loadSelectedBuildings = useMemo(() => createBuildingsByIdsLoader(), []);
    const loadSelectedAccounts = useMemo(() => createAccountsByIdsLoader(), []);
    const loadSelectedTerritories = useMemo(() => loadSelectedFromOptions(territoriesForSelect), [territoriesForSelect]);
    // --- Query + A11: View model (Series uses total_count not total) ---
    const seriesQuery = useSeriesQuery(state);
    const { rows, totalCount, totalPages, isError, refetch } = useListingQueryViewModel({
        query: seriesQuery,
        pageSize: state.page.size,
        getTotalCount: (d) => d.total_count
    });
    // Series uses isFetching (not isLoading) to show the loading indicator on every
    // in-flight request, including background refetches. Preserve this semantic explicitly.
    const isFetching = seriesQuery.isFetching;
    // --- A15: Publish list context bridge ---
    // Without this the assistant falls back to the server-rendered list_context,
    // which is built at HTML-render time — before this listing has fetched
    // anything — so it carries no filters and an unfiltered count.
    usePublishListContextBridge({
        page: state.page,
        sort: state.sort,
        filters: state.filters,
        search: state.search,
        totalCount,
        rows
    });
    // Row fingerprint drives the Table's selection resetKey. Composed with page
    // size so a page-size change always produces a different key even when the
    // leading id set would otherwise be a superset (e.g. 25→50 on page 1).
    const rowFingerprint = `${(_c = (_b = (_a = seriesQuery.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.map((r) => r.id).join(',')) !== null && _c !== void 0 ? _c : ''}|${state.page.number}|${state.page.size}|${(_e = (_d = state.sort) === null || _d === void 0 ? void 0 : _d.field) !== null && _e !== void 0 ? _e : ''}|${(_g = (_f = state.sort) === null || _f === void 0 ? void 0 : _f.direction) !== null && _g !== void 0 ? _g : ''}`;
    // Bulk Extend modal validation — attach once; stable handlers avoid listener accumulation.
    useEffect(() => {
        const yearsInput = document.getElementById('inspection_series_extend_by_years');
        const validationErrors = document.getElementById('validation-errors');
        const submitBtn = document.getElementById('bulk-extend-series-submit');
        const invalidYearsMessage = translate('table.bulk_extend_years_invalid', {
            namespace: NS,
            defaultValue: 'Value must be >= 1 and <= 10'
        });
        const handleYearsChange = () => {
            if (!yearsInput || !validationErrors)
                return;
            const val = parseInt(yearsInput.value, 10) || 0;
            validationErrors.textContent =
                val >= BULK_EXTEND_YEARS_MIN && val <= BULK_EXTEND_YEARS_MAX ? '' : invalidYearsMessage;
        };
        const handleSubmit = (ev) => {
            var _a;
            if (((_a = validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors.textContent) !== null && _a !== void 0 ? _a : '').length > 0) {
                ev.preventDefault();
            }
        };
        if (yearsInput) {
            yearsInput.addEventListener('change', handleYearsChange);
        }
        if (submitBtn) {
            submitBtn.addEventListener('click', handleSubmit);
        }
        return () => {
            if (yearsInput) {
                yearsInput.removeEventListener('change', handleYearsChange);
            }
            if (submitBtn) {
                submitBtn.removeEventListener('click', handleSubmit);
            }
        };
    }, []);
    // --- A5: Filter bar controllers ---
    // activeFilterKeys must come from this hook (stable useState reference).
    // Passing an inline `[]` into usePersistedListingPreferences re-creates the
    // array every render → effect deps change → savePreferences → setState → loop
    // (IP-15282 / Maximum update depth exceeded on Series / Building / Account).
    const { activeFilterKeys, handleFiltersChange, handleResetFilters, handleResetCache } = useListingFilterBarControllers({
        setFilters,
        initialFilters: INITIAL_SERIES_FILTERS,
        initialActiveFilterKeys: [],
        clearPreferences
    });
    // --- A6: Debounced search ---
    const { searchInput, handleSearchChange } = useDebouncedListingSearch({
        setSearch
    });
    // --- A7: Table navigation controllers ---
    const { handleSort, handlePageChange, handlePageSizeChange } = useListingTableControllers({
        setSort,
        setPageNumber,
        setPageSize
    });
    // --- A10: Column visibility state ---
    // Wrap column builder to match hook's generic TableColumn<Record<string,unknown>> signature.
    const buildVisibleSeriesColumnsCasted = useCallback((keys) => buildVisibleSeriesColumns(keys, setEditingSeries), []);
    const buildVisibilityItemsFn = useCallback((p) => buildSeriesColumnVisibilityItems(p.allowed, p.required), []);
    const { visibleColumnKeys, visibleColumns: visibleColumnsRaw, columnVisibilityItems, sortableColumns, handleColumnVisibilityApply } = useListingColumnVisibilityState({
        policy,
        initialVisibleColumnKeys,
        buildVisibleColumns: buildVisibleSeriesColumnsCasted,
        buildVisibilityItems: buildVisibilityItemsFn,
        nonSortableKeys: [...SERIES_NON_SORTABLE_KEYS]
    });
    // Re-type for Table<SeriesRow> which needs the domain-specific column type.
    const visibleColumns = visibleColumnsRaw;
    // --- A3: Persist preferences ---
    usePersistedListingPreferences({
        savePreferences,
        pageSize: state.page.size,
        sort: state.sort,
        filters: state.filters,
        activeFilterKeys,
        visibleColumnKeys
    });
    // --- Filter config (page-specific) ---
    const { filters: filterConfigs } = useSeriesFilterConfig({
        scope,
        filters: state.filters,
        onChange: handleFiltersChange,
        hasShowBuildingFilter,
        hasShowAccountFilter,
        hasShowExpirationDateFilter,
        hasShowStatusFilter,
        hasShowAutoExtendFilter,
        hasShowExpiredSeriesFilter,
        hasDisableAllFilters,
        loadBuildings,
        loadAccounts,
        loadTerritories,
        loadSelectedBuildings,
        loadSelectedAccounts,
        loadSelectedTerritories
    });
    // --- Bulk Extend (domain-specific ERB modal integration) ---
    const bulkExtendInFlightRef = useRef(false);
    const [bulkExtendPending, setBulkExtendPending] = useState(false);
    const handleBulkExtend = useCallback((e, selection) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        e.preventDefault();
        if (bulkExtendInFlightRef.current)
            return;
        if (!selection.selectedIds.length && !selection.selectAllEntire) {
            // eslint-disable-next-line no-alert
            window.alert(translate('table.select_series', { namespace: NS }));
            return;
        }
        let ids = selection.selectedIds;
        if (selection.selectAllEntire && totalCount > 0) {
            bulkExtendInFlightRef.current = true;
            setBulkExtendPending(true);
            try {
                const all = yield fetchInspectionSeries(Object.assign(Object.assign({}, state), { page: { number: 1, size: Math.max(totalCount, 1) } }));
                ids = ((_a = all.data) !== null && _a !== void 0 ? _a : []).map((row) => row.id);
            }
            catch (_b) {
                // eslint-disable-next-line no-alert
                window.alert(I18n.t('generic.error'));
                return;
            }
            finally {
                bulkExtendInFlightRef.current = false;
                setBulkExtendPending(false);
            }
        }
        if (!ids.length) {
            // eslint-disable-next-line no-alert
            window.alert(translate('table.select_series', { namespace: NS }));
            return;
        }
        const idsInput = document.getElementById('inspection_series_ids');
        if (idsInput) {
            idsInput.value = ids.join(',');
        }
        const { $ } = window;
        if (typeof $ === 'function') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            $('#bulk-extend-series-modal').modal({ keyboard: false }).show();
        }
    }), [totalCount, state]);
    // ---------------------------------------------------------------------------
    // WorkspaceHeader action (standalone only)
    // ---------------------------------------------------------------------------
    const newSeriesAction = useMemo(() => {
        if (!showHeader || !newSeriesLabel || !newSeriesUrl)
            return undefined;
        if (canCreateSeries) {
            return (React.createElement("a", { href: newSeriesUrl, className: "qmb-ui-button qmb-ui-button--primary" },
                React.createElement("i", { className: "fa-light fa-circle-plus" }),
                ` ${newSeriesLabel}`));
        }
        return (React.createElement("span", { "data-toggle": "popover", "data-html": "true", "data-content": newSeriesForbiddenMessage, "data-placement": "bottom" },
            React.createElement("button", { type: "button", className: "qmb-ui-button qmb-ui-button--primary", disabled: true },
                React.createElement("i", { className: "fa-light fa-circle-plus" }),
                ` ${newSeriesLabel}`)));
    }, [showHeader, newSeriesLabel, canCreateSeries, newSeriesUrl, newSeriesForbiddenMessage]);
    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    const tableErrorContent = (React.createElement("tr", null,
        React.createElement("td", { colSpan: visibleColumns.length + 1, className: "series-listing__error" },
            React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
            React.createElement("span", null, I18n.t('generic.error')),
            React.createElement("button", { type: "button", className: "qmb-control--sm", onClick: () => {
                    void refetch();
                } }, I18n.t('generic.retry')))));
    const tableEmptyContent = (React.createElement("tr", null,
        React.createElement("td", { colSpan: visibleColumns.length + 1, className: "series-listing__empty" },
            React.createElement("span", null, translate('empty_state', { namespace: NS, defaultValue: 'No inspection series found.' })))));
    const hasBulkActions = !hasDisableAllBulkActions;
    return (React.createElement(KendoIntlProvider, null,
        showHeader && pageTitle && (React.createElement(WorkspaceHeader, { divider: false, title: pageTitle, subtitle: headerSubtitle || undefined, actions: newSeriesAction })),
        React.createElement(Table, { data: rows, columns: visibleColumns, variant: "listing", size: TableSize.Full, selectable: true, getItemId: (row) => row.id, sortable: true, sortableColumns: sortableColumns, onSort: handleSort, sortBy: (_j = (_h = state.sort) === null || _h === void 0 ? void 0 : _h.field) !== null && _j !== void 0 ? _j : null, sortDirection: ((_l = (_k = state.sort) === null || _k === void 0 ? void 0 : _k.direction) !== null && _l !== void 0 ? _l : null), onPageChange: handlePageChange, serverCurrentPage: state.page.number, serverTotalPages: totalPages, serverTotalItems: totalCount, pageSize: state.page.size, pageSizeOptions: [...SERIES_PAGE_SIZES], onPageSizeChange: handlePageSizeChange, isLoading: isFetching, isError: isError, errorContent: tableErrorContent, emptyContent: tableEmptyContent, enableCustomScrollbar: true, selection: {
                mode: 'entire',
                selectAllLabel: translate('table.selected_global_counter', {
                    namespace: NS,
                    count: totalCount
                }),
                resetKey: rowFingerprint
            }, toolbar: hasBulkActions
                ? {
                    groups: [
                        {
                            id: 'series-bulk-actions',
                            content: (selection) => (React.createElement(Button, { onClick: (e) => handleBulkExtend(e, selection), disabled: (selection.selectedIds.length === 0 && !selection.selectAllEntire) || bulkExtendPending },
                                React.createElement("i", { className: "fa-light fa-rectangle-history-circle-plus" }),
                                ` ${translate('table.buttons.bulk_extend', { namespace: NS })}`))
                        }
                    ]
                }
                : undefined, filterBar: !hasDisableAllFilters ? (React.createElement(FilterBar, { filters: filterConfigs, onResetFilters: handleResetFilters, showSearch: true, searchValue: searchInput, onSearchChange: handleSearchChange, searchPlaceholder: I18n.t('filters.search'), allowResetCache: process.env.RAILS_ENV !== 'production', onResetCache: handleResetCache })) : undefined, columnVisibility: {
                items: columnVisibilityItems,
                visibleKeys: visibleColumnKeys,
                defaultKeys: policy.defaults,
                onApply: handleColumnVisibilityApply
            } }),
        editingSeries && editingSeries.buildingId !== null && (React.createElement(EditEndDateModal, { key: editingSeries.id, inspectionSeriesId: editingSeries.id, buildingId: editingSeries.buildingId, currentEndDate: editingSeries.endDate, minEndDate: minEndDate, maxEndDate: maxEndDate, autoOpen: true, renderTrigger: false, onClose: () => setEditingSeries(null), onSaved: (message) => {
                setEditingSeries(null);
                showSuccessToast(message);
                void refetch();
            } }))));
}
InspectionSeriesListing.propTypes = {
    buildingId: PropTypes.number,
    accountId: PropTypes.number,
    pageSize: PropTypes.number,
    territoriesForSelect: PropTypes.array,
    showHeader: PropTypes.bool,
    pageTitle: PropTypes.string,
    headerSubtitle: PropTypes.string,
    newSeriesUrl: PropTypes.string,
    canCreateSeries: PropTypes.bool,
    newSeriesLabel: PropTypes.string,
    newSeriesForbiddenMessage: PropTypes.string,
    hasShowBuildingFilter: PropTypes.bool,
    hasShowAccountFilter: PropTypes.bool,
    hasShowExpirationDateFilter: PropTypes.bool,
    hasShowStatusFilter: PropTypes.bool,
    hasShowAutoExtendFilter: PropTypes.bool,
    hasShowExpiredSeriesFilter: PropTypes.bool,
    hasDisableAllFilters: PropTypes.bool,
    hasDisableAllBulkActions: PropTypes.bool,
    minEndDate: PropTypes.string,
    maxEndDate: PropTypes.string
};
